import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Sampling Distribution of the Sample Mean (CLT)
// ─────────────────────────────────────────────────────────────────
function DiagramSamplingDistribution() {
  const W = 900, H = 330;

  // erf approximation for normal CDF
  function erf(x) {
    const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
    const sign=x<0?-1:1; const t=1/(1+p*Math.abs(x));
    const y=1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
    return sign*y;
  }
  const normcdf = x => 0.5*(1+erf(x/Math.sqrt(2)));
  const normpdf = (x,mu,sig) => (1/(sig*Math.sqrt(2*Math.PI)))*Math.exp(-0.5*((x-mu)/sig)**2);

  // Show three sample sizes: n=1 (uniform), n=5, n=30
  const mu=0, sigma=1;
  const cases = [
    { n:1,   se:1,     color:'#c0392b', label:'n=1  (original)',  lbl2:'σ/√1 = 1.00' },
    { n:5,   se:1/Math.sqrt(5),  color:'#e07b00', label:'n=5',   lbl2:`σ/√5 ≈ ${(1/Math.sqrt(5)).toFixed(2)}` },
    { n:30,  se:1/Math.sqrt(30), color:'#2E75B6', label:'n=30',  lbl2:`σ/√30 ≈ ${(1/Math.sqrt(30)).toFixed(2)}` },
  ];

  const aX=60, aY=30, aW=480, aH=220;
  const xMin=-3.2, xMax=3.2;
  const toX = x => aX + (x-xMin)/(xMax-xMin)*aW;
  const maxDens = normpdf(0,0,cases[2].se);
  const toY = y => aY + aH - (y/maxDens)*aH;

  const panX=570, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Sampling Distribution of X̄ — Central Limit Theorem in Action</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: overlaid sampling distributions */}
        <rect x={10} y={8} width={540} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={532} height={22} rx={3} fill="#1F3864"/>
        <text x={280} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          How X̄ concentrates around μ as sample size n grows
        </text>

        {/* Grid lines */}
        {[0.2,0.4,0.6,0.8,1.0].map(frac=>{
          const yv = aY + aH*(1-frac);
          return <line key={frac} x1={aX} y1={yv} x2={aX+aW} y2={yv} stroke="#eee" strokeWidth="0.8"/>;
        })}

        {/* Baseline & y-axis */}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY} x2={aX} y2={aY+aH} stroke="#aaa" strokeWidth="1"/>

        {/* X ticks */}
        {[-3,-2,-1,0,1,2,3].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#aaa" strokeWidth="1"/>
            <text x={toX(v)} y={aY+aH+16} textAnchor="middle" fontSize="10" fill="#666">{v}</text>
          </g>
        ))}
        <text x={aX+aW/2} y={aY+aH+30} textAnchor="middle" fontSize="11" fill="#555">x̄  (sample mean)</text>
        <text x={aX-12} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-12},${aY+aH/2})`}>density</text>

        {/* μ line */}
        <line x1={toX(0)} y1={aY} x2={toX(0)} y2={aY+aH} stroke="#888" strokeWidth="1.2" strokeDasharray="6,3"/>
        <text x={toX(0)+4} y={aY+12} fontSize="9" fill="#888">μ=0</text>

        {/* Curves */}
        {cases.map(({ n, se, color }) => {
          const pts = Array.from({length:120},(_,i)=>{
            const x = xMin+(xMax-xMin)*i/119;
            return `${toX(x).toFixed(1)},${toY(normpdf(x,mu,se)).toFixed(1)}`;
          }).join(' ');
          return <polyline key={n} points={pts} fill="none" stroke={color} strokeWidth={n===30?2.8:2} opacity="0.9"/>;
        })}

        {/* Legend */}
        {cases.map(({ n, color, label, lbl2 }, i) => (
          <g key={n}>
            <line x1={aX+8} y1={aY+aH+46+i*18} x2={aX+28} y2={aY+aH+46+i*18} stroke={color} strokeWidth="2.5"/>
            <text x={aX+34} y={aY+aH+50+i*18} fontSize="10" fill={color} fontWeight="700">{label}  —  SE={lbl2}</text>
          </g>
        ))}

        {/* RIGHT panel: CLT statement boxes */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Central Limit Theorem
        </text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'Statement',
            lines:['If X₁,…,Xₙ are i.i.d. with','mean μ and variance σ²,','then as n→∞:','X̄ ~ N(μ, σ²/n)'] },
          { y:140, bg:'#f0fff4', bc:'#27ae60',
            title:'Standard Error',
            lines:['SE(X̄) = σ/√n','Spread of X̄ shrinks as','n grows — precision','improves with √n.'] },
          { y:234, bg:'#fff7ee', bc:'#e07b00',
            title:'Rule of Thumb',
            lines:['n ≥ 30 usually sufficient','n ≥ 15 if dist. is symmetric','Skewed distributions','need larger n.'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={90} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="11"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The Central Limit Theorem guarantees that the sampling distribution of X̄ is approximately Normal regardless of the population's shape, provided n is large enough. The spread of this distribution — the standard error σ/√n — shrinks as sample size grows, explaining why larger samples give more precise estimates. The curves above show the dramatic tightening as n increases from 1 to 30.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Point Estimation: Bias, Variance, MSE
// ─────────────────────────────────────────────────────────────────
function DiagramPointEstimation() {
  const W = 900, H = 310;
  const panW = (W-28)/3;

  // Target diagrams for 4 estimator types
  const targets = [
    { title:'Low Bias\nLow Variance', sub:'Ideal estimator', color:'#27ae60',
      dots:[{x:0,y:0},{x:3,y:-2},{x:-2,y:3},{x:1,y:2},{x:-1,y:-1}] },
    { title:'Low Bias\nHigh Variance', sub:'Unbiased but imprecise', color:'#e07b00',
      dots:[{x:-18,y:12},{x:14,y:-20},{x:8,y:22},{x:-16,y:-8},{x:20,y:10}] },
    { title:'High Bias\nLow Variance', sub:'Precise but wrong center', color:'#c0392b',
      dots:[{x:22,y:-8},{x:20,y:-4},{x:24,y:-10},{x:18,y:-6},{x:23,y:-12}] },
  ];

  const tR=55, dotR=4;
  const panH=H-16;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Point Estimation — Bias, Variance, and the Bias–Variance Trade-off</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {targets.map(({ title, sub, color, dots }, pi) => {
          const px = 10 + pi*(panW+4);
          const cx = px + panW/2;
          const cy = 130;

          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={panH} rx={5} fill="#fff" stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={36} rx={3} fill={color}/>
              {title.split('\n').map((ln,j)=>(
                <text key={j} x={px+panW/2} y={26+j*14} textAnchor="middle" fontSize="11.5" fill="#fff" fontWeight="700">{ln}</text>
              ))}
              <text x={px+panW/2} y={56} textAnchor="middle" fontSize="10" fill={color}>{sub}</text>

              {/* Target rings */}
              {[tR, tR*0.65, tR*0.35].map((r,ri)=>(
                <circle key={ri} cx={cx} cy={cy} r={r} fill="none"
                  stroke={ri===0?'#ddd':ri===1?'#ccc':'#bbb'} strokeWidth="1.5"/>
              ))}
              {/* Crosshairs */}
              <line x1={cx-tR-4} y1={cy} x2={cx+tR+4} y2={cy} stroke="#e0e0e0" strokeWidth="1"/>
              <line x1={cx} y1={cy-tR-4} x2={cx} y2={cy+tR+4} stroke="#e0e0e0" strokeWidth="1"/>
              {/* True value marker */}
              <circle cx={cx} cy={cy} r={5} fill="#1F3864"/>
              <text x={cx+7} y={cy-7} fontSize="9" fill="#1F3864" fontWeight="700">True θ</text>

              {/* Estimate dots */}
              {dots.map((d,i)=>(
                <circle key={i} cx={cx+d.x} cy={cy+d.y} r={dotR} fill={color} opacity="0.8"/>
              ))}

              {/* Centroid (mean of estimates) */}
              {(() => {
                const mx = dots.reduce((s,d)=>s+d.x,0)/dots.length;
                const my = dots.reduce((s,d)=>s+d.y,0)/dots.length;
                return (
                  <g>
                    <circle cx={cx+mx} cy={cy+my} r={6} fill="none" stroke={color} strokeWidth="2.2"/>
                    <line x1={cx+mx-5} y1={cy+my} x2={cx+mx+5} y2={cy+my} stroke={color} strokeWidth="1.5"/>
                    <line x1={cx+mx} y1={cy+my-5} x2={cx+mx} y2={cy+my+5} stroke={color} strokeWidth="1.5"/>
                  </g>
                );
              })()}

              {/* Formula box */}
              <rect x={px+8} y={cy+tR+14} width={panW-16} height={44} rx={3} fill="#f8fbff" stroke={color} strokeWidth="1"/>
              {pi===0 && <>
                <text x={px+panW/2} y={cy+tR+28} textAnchor="middle" fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">MSE = Bias²+Var → small</text>
                <text x={px+panW/2} y={cy+tR+42} textAnchor="middle" fontSize="9.5" fill="#555">Best achievable: unbiased</text>
                <text x={px+panW/2} y={cy+tR+54} textAnchor="middle" fontSize="9.5" fill="#555">and minimum variance (UMVUE)</text>
              </>}
              {pi===1 && <>
                <text x={px+panW/2} y={cy+tR+28} textAnchor="middle" fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">Bias=0, Var(θ̂) high</text>
                <text x={px+panW/2} y={cy+tR+42} textAnchor="middle" fontSize="9.5" fill="#555">E[θ̂]=θ but estimates</text>
                <text x={px+panW/2} y={cy+tR+54} textAnchor="middle" fontSize="9.5" fill="#555">scatter widely</text>
              </>}
              {pi===2 && <>
                <text x={px+panW/2} y={cy+tR+28} textAnchor="middle" fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">Bias=E[θ̂]−θ ≠ 0</text>
                <text x={px+panW/2} y={cy+tR+42} textAnchor="middle" fontSize="9.5" fill="#555">Systematically misses</text>
                <text x={px+panW/2} y={cy+tR+54} textAnchor="middle" fontSize="9.5" fill="#555">true value despite precision</text>
              </>}
            </g>
          );
        })}

        {/* MSE decomposition panel */}
        {(() => {
          const px = 10 + 3*(panW+4);
          // No 4th panel — instead put a summary bar at bottom
          return null;
        })()}

        {/* Bottom summary strip */}
        <rect x={10} y={H-44} width={W-20} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={W/2} y={H-27} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
          MSE(θ̂) = Bias(θ̂)² + Var(θ̂)   —   Bias = E[θ̂] − θ   —   An unbiased estimator has E[θ̂] = θ for all θ
        </text>
        <text x={W/2} y={H-13} textAnchor="middle" fontSize="10" fill="#555">
          Cramér–Rao lower bound: Var(θ̂) ≥ 1/I(θ)  where I(θ) is Fisher information — no unbiased estimator can beat this
        </text>
      </svg>
      <div style={S.dCaption}>
        Each dot represents one estimate θ̂ from a different sample; the cross marks the true parameter θ. Bias measures systematic error (is the mean of estimates on target?); variance measures random scatter. MSE = Bias² + Var combines both into a single loss. A biased estimator with low variance can have lower MSE than an unbiased one with high variance — this is the bias–variance trade-off.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Confidence Intervals
// ─────────────────────────────────────────────────────────────────
function DiagramConfidenceIntervals() {
  const W = 900, H = 330;

  // Left panel: show 20 simulated 95% CIs, some miss
  const seed = 42;
  // Deterministic "random" CIs around μ=0, SE=1, z*=1.96
  const mu=0, zstar=1.96;
  const ciData = [
    {est:0.14},{est:-0.88},{est:0.52},{est:-0.31},{est:0.77},
    {est:-1.44},{est:0.23},{est:2.21},{est:-0.66},{est:0.41},
    {est:0.88},{est:-0.19},{est:-1.12},{est:0.63},{est:-2.31},
    {est:0.37},{est:-0.54},{est:1.08},{est:0.02},{est:-0.79},
  ];

  const lPanW=420, lPanH=H-20;
  const ciAreaX=80, ciAreaY=32, ciAreaW=lPanW-100, ciAreaH=lPanH-60;
  const rowH = ciAreaH/ciData.length;
  const toX = v => ciAreaX + (v+3)/6*ciAreaW; // maps -3..3 → area
  const trueX = toX(mu);

  // Right panel: CI interpretation
  const rPanX=450;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Confidence Intervals — 20 Simulated 95% CIs (each from a different sample)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT panel */}
        <rect x={10} y={10} width={lPanW} height={lPanH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={14} width={lPanW-8} height={18} rx={3} fill="#2E75B6"/>
        <text x={lPanW/2+10} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
          95% CI: x̄ ± 1.96·SE  (n=30, σ known, μ=0)
        </text>

        {/* True μ vertical line */}
        <line x1={trueX} y1={ciAreaY} x2={trueX} y2={ciAreaY+ciAreaH} stroke="#1F3864" strokeWidth="2" strokeDasharray="6,3"/>
        <text x={trueX+4} y={ciAreaY-2} fontSize="9" fill="#1F3864" fontWeight="700">True μ=0</text>

        {/* X-axis */}
        {[-2,-1,0,1,2].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={ciAreaY+ciAreaH} x2={toX(v)} y2={ciAreaY+ciAreaH+5} stroke="#aaa" strokeWidth="1"/>
            <text x={toX(v)} y={ciAreaY+ciAreaH+15} textAnchor="middle" fontSize="9" fill="#666">{v}</text>
          </g>
        ))}

        {/* CIs */}
        {ciData.map(({ est }, i) => {
          const lo = est - zstar, hi = est + zstar;
          const captures = lo <= mu && mu <= hi;
          const color = captures ? '#2E75B6' : '#c0392b';
          const cy = ciAreaY + i*rowH + rowH/2;
          const x1 = toX(lo), x2 = toX(hi), ex = toX(est);
          // Clamp to area
          const cx1 = Math.max(ciAreaX, x1), cx2 = Math.min(ciAreaX+ciAreaW, x2);
          return (
            <g key={i}>
              <line x1={cx1} y1={cy} x2={cx2} y2={cy} stroke={color} strokeWidth={captures?2:2.5} opacity="0.8"/>
              <line x1={cx1} y1={cy-4} x2={cx1} y2={cy+4} stroke={color} strokeWidth="1.5"/>
              <line x1={cx2} y1={cy-4} x2={cx2} y2={cy+4} stroke={color} strokeWidth="1.5"/>
              <circle cx={ex} cy={cy} r={3} fill={color}/>
              {!captures && <text x={ciAreaX+ciAreaW+6} y={cy+4} fontSize="9" fill="#c0392b" fontWeight="700">✗</text>}
            </g>
          );
        })}

        {/* Count */}
        {(() => {
          const misses = ciData.filter(({est})=> {
            const lo=est-zstar,hi=est+zstar; return !(lo<=mu&&mu<=hi);
          }).length;
          return (
            <text x={lPanW/2+10} y={ciAreaY+ciAreaH+30} textAnchor="middle" fontSize="10" fill="#555">
              {20-misses}/20 CIs capture μ  ·  {misses} miss (red) — expected ≈ 1 miss per 20 at 95%
            </text>
          );
        })()}

        {/* RIGHT panel */}
        <rect x={rPanX} y={10} width={W-rPanX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={rPanX+(W-rPanX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          CI Interpretation
        </text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            title:'Correct Interpretation',
            lines:['If we repeated this procedure','many times, 95% of CIs','constructed this way would','contain the true parameter.'] },
          { y:138, bg:'#fff0f0', bc:'#c0392b',
            title:'Common Mistake',
            lines:['"95% chance μ is in this CI"','is WRONG. μ is fixed (not','random). The CI is random.','This specific CI either does','or does not contain μ.'] },
          { y:250, bg:'#f0fff4', bc:'#27ae60',
            title:'Width of CI',
            lines:['Width = 2·z*·(σ/√n)','Narrow CI: large n, small σ','Wider z* (99%) → wider CI','Trade off confidence vs width'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={rPanX+8} y={y} width={W-rPanX-30} height={104} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={rPanX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={rPanX+18} y={y+32+j*18} fontSize="10.5" fill={j===0?bc:'#444'}
                fontWeight={j===0?'600':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Each horizontal line is one 95% CI constructed from a different sample. Blue intervals capture the true μ=0; red ones miss it. Across many repetitions, exactly 95% should capture μ — the confidence level is a property of the procedure, not of any single interval. The correct interpretation concerns long-run frequency, not probability for a fixed interval.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Hypothesis Testing: p-value and Critical Region
// ─────────────────────────────────────────────────────────────────
function DiagramHypothesisTesting() {
  const W = 900, H = 330;

  function erf(x) {
    const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
    const sign=x<0?-1:1; const t=1/(1+p*Math.abs(x));
    const y=1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
    return sign*y;
  }
  const normcdf = x => 0.5*(1+erf(x/Math.sqrt(2)));
  const normpdf = x => (1/Math.sqrt(2*Math.PI))*Math.exp(-0.5*x*x);

  // Two-tailed z-test, alpha=0.05, z_obs=2.1
  const alpha=0.05, zObs=2.1, zCrit=1.96;
  const pval = 2*(1-normcdf(Math.abs(zObs)));

  const aX=50, aY=30, aW=500, aH=200;
  const xMin=-4, xMax=4;
  const toX = x => aX+(x-xMin)/(xMax-xMin)*aW;
  const maxDens = normpdf(0);
  const toY = y => aY+aH-(y/maxDens)*aH;

  const pts120 = Array.from({length:120},(_,i)=>{ const x=xMin+(xMax-xMin)*i/119; return {x,y:normpdf(x)}; });
  const polyAll = pts120.map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');

  // Left critical region shade
  const leftShade = [
    `${toX(-xMax)},${aY+aH}`,
    ...pts120.filter(p=>p.x<=-zCrit).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${toX(-zCrit)},${aY+aH}`,
  ].join(' ');

  // Right critical region shade
  const rightShade = [
    `${toX(zCrit)},${aY+aH}`,
    ...pts120.filter(p=>p.x>=zCrit).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${toX(xMax)},${aY+aH}`,
  ].join(' ');

  // p-value shade (beyond |z_obs| in both tails)
  const pShadeR = [
    `${toX(zObs)},${aY+aH}`,
    ...pts120.filter(p=>p.x>=zObs).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${toX(xMax)},${aY+aH}`,
  ].join(' ');
  const pShadeL = [
    `${toX(-xMax)},${aY+aH}`,
    ...pts120.filter(p=>p.x<=-zObs).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${toX(-zObs)},${aY+aH}`,
  ].join(' ');

  const panX=575;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Hypothesis Testing — Null Distribution, p-value, and Critical Region (two-tailed, α=0.05)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: null distribution */}
        <rect x={10} y={8} width={555} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={547} height={20} rx={3} fill="#1F3864"/>
        <text x={287} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
          Null distribution Z~N(0,1) under H₀: μ=0  |  Observed z={zObs}  |  p-value≈{pval.toFixed(3)}
        </text>

        {/* Grid */}
        {[0.1,0.2,0.3,0.4].map(v=>(
          <line key={v} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY} x2={aX} y2={aY+aH} stroke="#aaa" strokeWidth="1"/>

        {/* Critical region shading (red) */}
        <polygon points={leftShade} fill="#c0392b33"/>
        <polygon points={rightShade} fill="#c0392b33"/>

        {/* p-value shading (darker) */}
        <polygon points={pShadeR} fill="#c0392b66"/>
        <polygon points={pShadeL} fill="#c0392b66"/>

        {/* Curve */}
        <polyline points={polyAll} fill="none" stroke="#2E75B6" strokeWidth="2.5"/>

        {/* Critical value lines */}
        {[-zCrit, zCrit].map(zc=>(
          <g key={zc}>
            <line x1={toX(zc)} y1={aY} x2={toX(zc)} y2={aY+aH} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
            <text x={toX(zc)} y={aY+aH+15} textAnchor="middle" fontSize="9" fill="#c0392b" fontWeight="700">
              {zc>0?`+${zCrit}`:zc}
            </text>
          </g>
        ))}

        {/* Observed z line */}
        <line x1={toX(zObs)} y1={aY} x2={toX(zObs)} y2={aY+aH} stroke="#e07b00" strokeWidth="2.2"/>
        <text x={toX(zObs)+4} y={aY+22} fontSize="10" fill="#e07b00" fontWeight="700">z={zObs}</text>
        <text x={toX(zObs)+4} y={aY+35} fontSize="9" fill="#e07b00">(observed)</text>

        {/* X ticks */}
        {[-3,-2,-1,0,1,2,3].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#ccc" strokeWidth="1"/>
            <text x={toX(v)} y={aY+aH+15} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        <text x={aX+aW/2} y={aY+aH+28} textAnchor="middle" fontSize="10" fill="#555">z  (test statistic)</text>

        {/* Labels for regions */}
        <text x={toX(-3)} y={aY+aH-12} fontSize="9" fill="#c0392b" fontWeight="700" textAnchor="middle">Reject H₀</text>
        <text x={toX(3)} y={aY+aH-12} fontSize="9" fill="#c0392b" fontWeight="700" textAnchor="middle">Reject H₀</text>
        <text x={toX(0)} y={aY+aH-12} fontSize="9" fill="#2E75B6" fontWeight="700" textAnchor="middle">Fail to reject H₀</text>

        {/* α/2 labels */}
        <text x={toX(-3.2)} y={aY+60} fontSize="9" fill="#c0392b">α/2=2.5%</text>
        <text x={toX(2.8)} y={aY+60} fontSize="9" fill="#c0392b">α/2=2.5%</text>

        {/* p-value annotation */}
        <text x={aX+aW/2} y={aY+aH+42} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">
          p-value = {pval.toFixed(3)} &lt; α=0.05  →  Reject H₀ at 5% significance level
        </text>

        {/* RIGHT panel */}
        <rect x={panX} y={8} width={W-panX-14} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Testing Logic
        </text>

        {[
          { y:32,  bg:'#EBF3FB', bc:'#2E75B6',  title:'H₀ and H₁',
            lines:['H₀: null hypothesis','(status quo / no effect)','H₁: alternative','(what we seek to show)'] },
          { y:120, bg:'#fff7ee', bc:'#e07b00',  title:'p-value',
            lines:['P(|Z|≥|z_obs| | H₀ true)','Probability of data this','extreme if H₀ is true.','Small p → evidence vs H₀'] },
          { y:208, bg:'#fff0f0', bc:'#c0392b',  title:'Two Error Types',
            lines:['Type I: reject true H₀  (α)','Type II: keep false H₀  (β)','Power = 1−β','α↓ → β↑ (trade-off)'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={82} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+30+j*16} fontSize="10" fill={j===0?bc:'#444'}
                fontWeight={j===0?'600':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The null distribution shows all possible test statistics if H₀ were true. The red shaded tails are the critical region (total area = α=0.05); the darker orange-red shading marks the p-value — the probability of seeing a z as extreme as 2.1 under H₀. Because z=2.1 falls in the critical region and p=0.038 {'<'} 0.05, we reject H₀. The p-value is not the probability H₀ is true — it is computed assuming H₀ is true.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Type I / II Errors and Power
// ─────────────────────────────────────────────────────────────────
function DiagramPower() {
  const W = 900, H = 330;

  function normpdf(x, mu=0, sig=1) {
    return (1/(sig*Math.sqrt(2*Math.PI)))*Math.exp(-0.5*((x-mu)/sig)**2);
  }

  // H0: μ=0, H1: μ=2, SE=1, alpha=0.05 one-tailed, zCrit=1.645
  const mu0=0, mu1=2, se=1, zCrit=1.645;
  const xMin=-3.5, xMax=6, pts=140;
  const maxDens = normpdf(0,0,1);

  const aX=50, aY=28, aW=520, aH=210;
  const toX = x => aX+(x-xMin)/(xMax-xMin)*aW;
  const toY = y => aY+aH-(y/maxDens)*aH;
  const critX = toX(zCrit);

  const mkPoly = (muV) => Array.from({length:pts},(_,i)=>{
    const x=xMin+(xMax-xMin)*i/(pts-1);
    return {x, y:normpdf(x,muV,se)};
  });

  const h0pts = mkPoly(mu0);
  const h1pts = mkPoly(mu1);

  const h0Poly = h0pts.map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');
  const h1Poly = h1pts.map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');

  // Alpha shade (right tail of H0 beyond zCrit)
  const alphaShade = [
    `${critX},${aY+aH}`,
    ...h0pts.filter(p=>p.x>=zCrit).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${toX(xMax)},${aY+aH}`,
  ].join(' ');

  // Beta shade (left tail of H1 below zCrit)
  const betaShade = [
    `${toX(xMin)},${aY+aH}`,
    ...h1pts.filter(p=>p.x<=zCrit).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${critX},${aY+aH}`,
  ].join(' ');

  // Power shade (right tail of H1 beyond zCrit)
  const powerShade = [
    `${critX},${aY+aH}`,
    ...h1pts.filter(p=>p.x>=zCrit).map(({x,y})=>`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`),
    `${toX(xMax)},${aY+aH}`,
  ].join(' ');

  const panX=595;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Statistical Power — Type I Error (α), Type II Error (β), and Power (1−β)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={575} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={567} height={20} rx={3} fill="#1F3864"/>
        <text x={297} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
          H₀: μ=0 vs H₁: μ=2, SE=1, one-tailed α=0.05, critical value=1.645
        </text>

        {/* Grid */}
        {[0.1,0.2,0.3,0.4].map(v=>(
          <line key={v} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>

        {/* Shaded regions */}
        <polygon points={alphaShade} fill="#c0392b55" />
        <polygon points={betaShade}  fill="#9b59b655" />
        <polygon points={powerShade} fill="#27ae6055" />

        {/* Curves */}
        <polyline points={h0Poly} fill="none" stroke="#2E75B6" strokeWidth="2.5"/>
        <polyline points={h1Poly} fill="none" stroke="#27ae60" strokeWidth="2.5"/>

        {/* Critical line */}
        <line x1={critX} y1={aY} x2={critX} y2={aY+aH} stroke="#e07b00" strokeWidth="2" strokeDasharray="6,3"/>
        <text x={critX+3} y={aY+14} fontSize="9" fill="#e07b00" fontWeight="700">zᶜ={zCrit}</text>

        {/* X ticks */}
        {[-3,-2,-1,0,1,2,3,4,5].map(v=>(
          v>=xMin&&v<=xMax ? (
            <g key={v}>
              <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#ccc" strokeWidth="1"/>
              <text x={toX(v)} y={aY+aH+15} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
            </g>
          ):null
        ))}
        <text x={aX+aW/2} y={aY+aH+27} textAnchor="middle" fontSize="10" fill="#555">test statistic</text>

        {/* Mean markers */}
        <line x1={toX(mu0)} y1={aY} x2={toX(mu0)} y2={aY+aH} stroke="#2E75B6" strokeWidth="1.2" strokeDasharray="4,3"/>
        <text x={toX(mu0)-4} y={aY+10} textAnchor="end" fontSize="9" fill="#2E75B6" fontWeight="700">μ₀=0</text>
        <line x1={toX(mu1)} y1={aY} x2={toX(mu1)} y2={aY+aH} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="4,3"/>
        <text x={toX(mu1)+4} y={aY+10} fontSize="9" fill="#27ae60" fontWeight="700">μ₁=2</text>

        {/* Region labels */}
        <text x={toX(2.6)} y={toY(0.05)-6} fontSize="10" fill="#c0392b" fontWeight="700" textAnchor="middle">α</text>
        <text x={toX(0.7)} y={toY(0.06)-6} fontSize="10" fill="#9b59b6" fontWeight="700" textAnchor="middle">β</text>
        <text x={toX(3.5)} y={toY(0.09)-6} fontSize="10" fill="#27ae60" fontWeight="700" textAnchor="middle">Power</text>

        {/* Legend */}
        {[
          {color:'#2E75B6',lbl:'H₀ distribution (μ=0)'},
          {color:'#27ae60',lbl:'H₁ distribution (μ=2)'},
          {color:'#c0392b',lbl:'α = P(reject H₀ | H₀ true) = 0.05'},
          {color:'#9b59b6',lbl:'β = P(fail to reject | H₁ true) ≈ 0.19'},
          {color:'#27ae60',lbl:'Power = 1−β ≈ 0.81'},
        ].map(({color,lbl},i)=>(
          <g key={i}>
            <rect x={aX} y={aY+aH+35+i*17} width={12} height={10} fill={color} opacity="0.8" rx={2}/>
            <text x={aX+16} y={aY+aH+44+i*17} fontSize="9.5" fill="#444">{lbl}</text>
          </g>
        ))}

        {/* RIGHT panel */}
        <rect x={panX} y={8} width={W-panX-14} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Power Analysis
        </text>

        {[
          { y:32,  bg:'#f0fff4', bc:'#27ae60',
            title:'Power = 1−β',
            lines:['P(reject H₀ | H₁ true)','Probability test correctly','detects a real effect.','Aim for power ≥ 0.80.'] },
          { y:130, bg:'#EBF3FB', bc:'#2E75B6',
            title:'Factors Increasing Power',
            lines:['Larger sample size n','Larger effect size |μ₁−μ₀|','Larger α (looser threshold)','Smaller σ (less noise)'] },
          { y:228, bg:'#fff7ee', bc:'#e07b00',
            title:'Sample Size Formula',
            lines:['n ≈ (z_α+z_β)²σ²/δ²','δ = desired detectable','effect size (μ₁−μ₀)','Pre-register before study!'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={92} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*17} fontSize="10" fill={j===0?bc:'#444'}
                fontWeight={j===0?'600':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Two sampling distributions overlap: the blue H₀ distribution and the green H₁ distribution. The critical value (orange dashed line) separates reject from fail-to-reject. The red area is α (falsely rejecting H₀); the purple area is β (missing a real effect); the green area is power. Moving the distributions further apart (larger effect) or increasing n (narrowing both curves) increases power while keeping α fixed.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Common Test Statistics and Decision Rules
// ─────────────────────────────────────────────────────────────────
function DiagramTestStatistics() {
  const W = 900, H = 360;

  const tests = [
    {
      name:'One-sample z-test', color:'#2E75B6', fill:'#EBF3FB',
      when:'Known σ, large n',
      stat:'Z = (X̄ − μ₀) / (σ/√n)',
      dist:'Z ~ N(0,1) under H₀',
      decision:'Reject H₀ if |Z| > z_{α/2}',
      example:'H₀: μ=100, n=50, σ=15, X̄=105\nz=(105-100)/(15/√50)=2.36\np≈0.018 < 0.05 → reject',
    },
    {
      name:'One-sample t-test', color:'#27ae60', fill:'#f0fff4',
      when:'Unknown σ, any n',
      stat:'T = (X̄ − μ₀) / (S/√n)',
      dist:'T ~ t(n−1) under H₀',
      decision:'Reject H₀ if |T| > t_{α/2, n−1}',
      example:'H₀: μ=0, n=20, S=2.3, X̄=1.1\nt=1.1/(2.3/√20)=2.14\nt_{.025,19}=2.09 → reject',
    },
    {
      name:'Two-sample t-test', color:'#e07b00', fill:'#fff7ee',
      when:'Compare two group means',
      stat:'T=(X̄₁−X̄₂)/SE_pooled',
      dist:'T ~ t(n₁+n₂−2) under H₀',
      decision:'Reject H₀ if |T| > t_{α/2, df}',
      example:'H₀: μ₁=μ₂\nn₁=n₂=25, pooled SE=0.8\nt=(5.2-4.7)/0.8=0.63\np=0.53 → fail to reject',
    },
    {
      name:'Chi-squared test', color:'#9b59b6', fill:'#f5d0ff',
      when:'Categorical: goodness of fit\nor test of independence',
      stat:'χ²=Σ(O−E)²/E',
      dist:'χ²(k−1) or χ²((r-1)(c-1))',
      decision:'Reject H₀ if χ² > χ²_{α, df}',
      example:'2×2 contingency table\ndf=(2-1)(2-1)=1\nχ²=5.8 > 3.84=χ²_{.05,1}\n→ reject independence',
    },
  ];

  const panW=(W-28)/4, panH=H-20;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Statistical Tests — Test Statistics, Distributions, and Decision Rules</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {tests.map(({ name, color, fill, when, stat, dist, decision, example }, pi) => {
          const px=10+pi*(panW+2.7);
          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={panH} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={36} rx={3} fill={color}/>
              <text x={px+panW/2} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{name}</text>
              <text x={px+panW/2} y={40} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">{when}</text>

              {/* Test statistic box */}
              <rect x={px+6} y={56} width={panW-12} height={26} rx={3} fill="#fff" stroke={color} strokeWidth="1.2"/>
              <text x={px+panW/2} y={66} textAnchor="middle" fontSize="8.5" fill="#888">Test statistic</text>
              <text x={px+panW/2} y={78} textAnchor="middle" fontSize="10.5"
                fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{stat}</text>

              {/* Distribution box */}
              <rect x={px+6} y={90} width={panW-12} height={26} rx={3} fill="#fff" stroke={color} strokeWidth="1.2"/>
              <text x={px+panW/2} y={100} textAnchor="middle" fontSize="8.5" fill="#888">Null distribution</text>
              <text x={px+panW/2} y={112} textAnchor="middle" fontSize="10"
                fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">{dist}</text>

              {/* Decision rule */}
              <rect x={px+6} y={124} width={panW-12} height={26} rx={3} fill={`${color}22`} stroke={color} strokeWidth="1"/>
              <text x={px+panW/2} y={134} textAnchor="middle" fontSize="8.5" fill="#888">Decision rule</text>
              <text x={px+panW/2} y={146} textAnchor="middle" fontSize="9.5"
                fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{decision}</text>

              {/* Example */}
              <rect x={px+6} y={158} width={panW-12} height={panH-170} rx={3} fill="#f8fbff" stroke="#ccd9ea" strokeWidth="1"/>
              <text x={px+panW/2} y={170} textAnchor="middle" fontSize="8.5" fill="#888">Example</text>
              {example.split('\n').map((ln,j)=>(
                <text key={j} x={px+12} y={184+j*18} fontSize="9.5"
                  fill="#444" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Each test standardizes the data into a test statistic whose distribution is known under H₀. The z-test requires known σ; the t-test estimates σ from the sample, using heavier-tailed t distributions that account for the extra uncertainty. Chi-squared tests compare observed to expected frequencies and do not assume normality for the data — only that cell counts are large enough (expected ≥ 5 rule of thumb).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Maximum Likelihood Estimation
// ─────────────────────────────────────────────────────────────────
function DiagramMLE() {
  const W = 900, H = 320;

  // Show log-likelihood for Normal mean (σ=1 known)
  // Data: x = {0.3, 1.1, 0.8, 1.6, 0.5} → sample mean = 0.86
  const data = [0.3, 1.1, 0.8, 1.6, 0.5];
  const n = data.length;
  const xbar = data.reduce((s,v)=>s+v,0)/n; // 0.86
  const sigma=1;

  // Log-likelihood: ℓ(μ) = -n/2·log(2πσ²) - Σ(xᵢ-μ)²/(2σ²)
  const loglik = mu => -n/2*Math.log(2*Math.PI) - data.reduce((s,v)=>s+(v-mu)**2,0)/(2*sigma*sigma);

  const muMin=-1, muMax=3;
  const nPts=120;
  const llPts = Array.from({length:nPts},(_,i)=>{
    const mu = muMin+(muMax-muMin)*i/(nPts-1);
    return { mu, ll: loglik(mu) };
  });
  const llMax = loglik(xbar);
  const llMin = Math.min(...llPts.map(p=>p.ll));

  const aX=70, aY=28, aW=460, aH=210;
  const toX = mu => aX+(mu-muMin)/(muMax-muMin)*aW;
  const toY = ll => aY+aH-(ll-llMin)/(llMax-llMin)*aH;

  const polyPts = llPts.map(({mu,ll})=>`${toX(mu).toFixed(1)},${toY(ll).toFixed(1)}`).join(' ');

  const panX=565;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Maximum Likelihood Estimation — Log-likelihood for Normal Mean (σ=1 known)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={545} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={537} height={20} rx={3} fill="#9b59b6"/>
        <text x={287} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
          Data: {'{0.3, 1.1, 0.8, 1.6, 0.5}'}   x̄ = {xbar.toFixed(2)}  ←  MLE = argmax ℓ(μ)
        </text>

        {/* Grid */}
        {[0.25,0.5,0.75].map(frac=>{
          const ll = llMin+(llMax-llMin)*frac;
          return (
            <g key={frac}>
              <line x1={aX} y1={toY(ll)} x2={aX+aW} y2={toY(ll)} stroke="#eee" strokeWidth="0.8"/>
              <text x={aX-5} y={toY(ll)+4} textAnchor="end" fontSize="8" fill="#aaa">{ll.toFixed(1)}</text>
            </g>
          );
        })}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY} x2={aX} y2={aY+aH} stroke="#aaa" strokeWidth="1"/>

        {/* Curve */}
        <polyline points={polyPts} fill="none" stroke="#9b59b6" strokeWidth="2.8"/>

        {/* MLE peak */}
        <line x1={toX(xbar)} y1={aY} x2={toX(xbar)} y2={aY+aH} stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3"/>
        <circle cx={toX(xbar)} cy={toY(llMax)} r={6} fill="#c0392b"/>
        <text x={toX(xbar)+8} y={toY(llMax)-4} fontSize="10" fill="#c0392b" fontWeight="700">MLE: μ̂={xbar.toFixed(2)}</text>

        {/* Data point ticks on x-axis */}
        {data.map((v,i)=>(
          <g key={i}>
            <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+8} stroke="#2E75B6" strokeWidth="1.5"/>
            <text x={toX(v)} y={aY+aH+19} textAnchor="middle" fontSize="9" fill="#2E75B6">{v}</text>
          </g>
        ))}

        {/* X ticks (μ axis) */}
        {[-0.5,0,0.5,1,1.5,2,2.5].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#ccc" strokeWidth="1"/>
            <text x={toX(v)} y={aY+aH+30} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        <text x={aX+aW/2} y={aY+aH+44} textAnchor="middle" fontSize="10" fill="#555">μ  (parameter value)</text>
        <text x={aX-40} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-40},${aY+aH/2})`}>ℓ(μ) = log L(μ)</text>

        {/* Data label */}
        <text x={aX+10} y={aY+aH+19} fontSize="8.5" fill="#2E75B6">← data xᵢ</text>

        {/* RIGHT panel */}
        <rect x={panX} y={8} width={W-panX-14} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          MLE Concepts
        </text>

        {[
          { y:32,  bg:'#f5d0ff', bc:'#9b59b6',
            title:'Likelihood L(θ|x)',
            lines:['L(θ|x) = ∏ f(xᵢ|θ)','Probability of observed','data as function of θ.','Maximize over θ.'] },
          { y:132, bg:'#EBF3FB', bc:'#2E75B6',
            title:'Log-likelihood ℓ(θ)',
            lines:['ℓ(θ) = Σ log f(xᵢ|θ)','Log converts product','to sum — easier to','maximize analytically.'] },
          { y:232, bg:'#f0fff4', bc:'#27ae60',
            title:'MLE Properties',
            lines:['Consistent: θ̂→θ as n→∞','Asymptotically Normal','Asymptotically efficient','(achieves Cramér–Rao)'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={W-panX-30} height={92} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*17} fontSize="10" fill={j===0?bc:'#444'}
                fontWeight={j===0?'600':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The log-likelihood curve ℓ(μ) shows how well each candidate value of μ explains the observed data. The MLE μ̂ = x̄ = 0.86 sits precisely at the peak — it is the value that makes the observed data most probable. For the Normal distribution with known σ, the MLE for μ is always the sample mean; in more complex models, numerical optimization is required to find the maximum.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function StatisticalInference() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Statistical Inference</h1>

      <p style={S.p}>
        Statistical inference is the process of drawing conclusions about a population from a sample. We observe data — a finite, random subset of all possible outcomes — and use it to estimate unknown parameters, quantify uncertainty, and test hypotheses about the underlying process. The two main frameworks are frequentist inference (parameters are fixed but unknown; probability describes long-run frequency) and Bayesian inference (parameters have prior distributions updated by evidence). This page covers the core ideas: sampling distributions, point and interval estimation, hypothesis testing, power analysis, and maximum likelihood.
      </p>

      <InfoBox
        title="The Inference Pipeline"
        items={[
          "Population: the complete set of units of interest, described by an unknown parameter θ (e.g. population mean μ, proportion p).",
          "Sample: n randomly selected observations X₁, …, Xₙ drawn from the population.",
          "Statistic: any function of the sample data (e.g. X̄, S²) — it has its own sampling distribution.",
          "Estimator θ̂: a statistic used to estimate θ. Its quality is measured by bias, variance, and MSE.",
          "Confidence interval: a range [L, U] constructed from the sample that covers θ with stated probability (the confidence level) across repeated sampling.",
          "Hypothesis test: a decision rule to reject or not reject a null hypothesis H₀ based on how unusual the observed data would be if H₀ were true.",
          "p-value: P(test statistic as extreme or more | H₀ true). Small p casts doubt on H₀; it is NOT P(H₀ is true).",
          "All inference assumes a probability model — conclusions depend on that model's assumptions being met.",
        ]}
      />

      <h2 style={S.h2}>Sampling Distribution and the Central Limit Theorem</h2>
      <DiagramSamplingDistribution />
      <Table2 rows={[
        ["Sampling distribution", "Distribution of a statistic over all possible samples of size n\nX̄ has mean μ and standard deviation σ/√n (the standard error)"],
        ["Standard error (SE)",   "SE(X̄) = σ/√n — measures precision of X̄ as an estimator of μ\nWith estimated σ̂: SE = S/√n (uses sample standard deviation)"],
        ["Central Limit Theorem", "X̄ ~ N(μ, σ²/n) approximately for large n, regardless of population shape\nEnables Normal-based CIs and tests for nearly any distribution"],
        ["Law of Large Numbers",  "X̄ → μ as n→∞ (almost surely)\nCLT tells us the rate: deviations are O(1/√n)"],
        ["Finite population",     "Sampling without replacement: SE multiplied by √((N−n)/(N−1))\nFinite population correction — matters when n/N > 0.05"],
        ["Bootstrap SE",          "Resample from data with replacement, compute statistic each time\nEmpirical approximation to sampling distribution when theory is hard"],
      ]} />

      <h2 style={S.h2}>Point Estimation — Bias, Variance, and MSE</h2>
      <DiagramPointEstimation />
      <Table2 rows={[
        ["Unbiased estimator",    "E[θ̂] = θ for all θ\nX̄ is unbiased for μ; S² = Σ(Xᵢ−X̄)²/(n−1) is unbiased for σ²"],
        ["MSE decomposition",     "MSE(θ̂) = Bias(θ̂)² + Var(θ̂)\nBiased estimators can have lower MSE if their variance is much smaller"],
        ["Consistency",           "θ̂ →ₚ θ as n→∞: estimator converges in probability to true value\nAll MLEs are consistent under mild regularity conditions"],
        ["Cramér–Rao bound",      "Var(θ̂) ≥ 1/I(θ) for any unbiased θ̂\nI(θ) = −E[d²logL/dθ²] is Fisher information — sets efficiency floor"],
        ["Sufficient statistic",  "T is sufficient if the conditional distribution of data given T doesn't depend on θ\nX̄ is sufficient for μ in a Normal model — retains all info about μ"],
        ["Method of moments",     "Set theoretical moments equal to sample moments; solve for parameters\nSimple but generally less efficient than MLE"],
      ]} />

      <h2 style={S.h2}>Confidence Intervals</h2>
      <DiagramConfidenceIntervals />
      <Table2 rows={[
        ["z-interval (known σ)",  "x̄ ± z_{α/2} · σ/√n\nz_{0.025}=1.960 for 95%; z_{0.005}=2.576 for 99%"],
        ["t-interval (unknown σ)","x̄ ± t_{α/2, n−1} · S/√n\nt distribution has heavier tails; approaches z as n→∞"],
        ["CI for proportion p",   "p̂ ± z_{α/2}·√(p̂(1−p̂)/n)  (Wald interval)\nUse when np̂≥5 and n(1−p̂)≥5; Wilson interval preferred otherwise"],
        ["Width of CI",           "Width = 2·z*·SE — wider interval ↔ higher confidence\nTo halve width: quadruple sample size (n scales as 1/width²)"],
        ["Interpretation",        "If procedure repeated: 95% of constructed CIs would contain θ\nNOT: 95% probability that this specific CI contains θ (θ is fixed)"],
        ["One-sided CI",          "Lower bound: θ̂ − z_α·SE  or upper bound: θ̂ + z_α·SE\nUsed when interest is only in upper/lower limit, e.g. safety margins"],
      ]} />

      <h2 style={S.h2}>Hypothesis Testing</h2>
      <DiagramHypothesisTesting />
      <Table2 rows={[
        ["Null hypothesis H₀",    "The default claim to be tested (e.g. μ=0, no effect, independence)\nInference is always conditional on H₀ being true"],
        ["p-value",               "P(|T| ≥ |t_obs| | H₀ true)\nDoes NOT equal P(H₀ true). A large p only means data is consistent with H₀"],
        ["Significance level α",  "Pre-specified threshold for p-value; common: α=0.05 or α=0.01\nControls Type I error rate (falsely rejecting H₀)"],
        ["Type I error (α)",      "Reject H₀ when it is actually true\nP(Type I error) = α — controlled by choice of significance level"],
        ["Type II error (β)",     "Fail to reject H₀ when H₁ is actually true\nP(Type II error) = β — depends on effect size and sample size"],
        ["Multiple testing",      "Testing k hypotheses at α: expected false positives = k·α\nBonferroni correction: use α/k per test; FDR control for large k"],
      ]} />

      <h2 style={S.h2}>Statistical Power</h2>
      <DiagramPower />
      <Table2 rows={[
        ["Power = 1−β",           "Probability of rejecting H₀ when H₁ is true\nPower depends on: sample size n, effect size δ, σ, and α"],
        ["Effect size",           "Standardized measure: Cohen's d = (μ₁−μ₀)/σ\nSmall d≈0.2, medium d≈0.5, large d≈0.8 (Cohen's conventions)"],
        ["Sample size formula",   "n ≈ (z_α + z_β)²σ²/δ²  (one-sample, one-tailed)\nDetermine before collecting data — pre-registration prevents p-hacking"],
        ["Power and α trade-off", "Lowering α (stricter threshold) reduces power for fixed n and δ\nMust balance false positive control vs ability to detect real effects"],
        ["A priori vs post hoc",  "A priori power analysis: plan n before study\nPost hoc: compute power after, using observed effect — often misleading"],
        ["Minimum detectable effect","MDE: smallest δ detectable with given n, α, power\nUseful when sample size is fixed by practical constraints"],
      ]} />

      <h2 style={S.h2}>Common Statistical Tests</h2>
      <DiagramTestStatistics />
      <Table2 rows={[
        ["One-sample z-test",     "Z=(X̄−μ₀)/(σ/√n); requires known σ or large n (CLT)\nTwo-tailed: reject if |Z|>z_{α/2}; one-tailed: if Z>z_α or Z<−z_α"],
        ["One-sample t-test",     "T=(X̄−μ₀)/(S/√n) ~ t(n−1) under H₀\nRobust to mild non-normality; exact for Normal data at any n"],
        ["Two-sample t-test",     "T=(X̄₁−X̄₂)/SE_pooled ~ t(n₁+n₂−2) if variances equal\nWelch's t: uses adjusted df when variances unequal — recommended default"],
        ["Paired t-test",         "T=D̄/(S_D/√n) where Dᵢ=X₁ᵢ−X₂ᵢ\nUse when observations are matched pairs; reduces between-subject variance"],
        ["Chi-squared test",      "χ²=Σ(O−E)²/E; df=(r−1)(c−1) for independence test\nExpected cell counts ≥5 required for good approximation"],
        ["F-test / ANOVA",        "F=MS_between/MS_within compares variances across ≥2 groups\nOne-way ANOVA generalizes two-sample t-test to k groups"],
      ]} />

      <h2 style={S.h2}>Maximum Likelihood Estimation</h2>
      <DiagramMLE />
      <Table2 rows={[
        ["Likelihood function",   "L(θ|x) = ∏ᵢ f(xᵢ|θ) — joint density as function of θ, not x\nTreats data as fixed; varies over parameter space"],
        ["Log-likelihood",        "ℓ(θ) = Σᵢ log f(xᵢ|θ) — log converts product to sum\nMLE: θ̂ = argmax ℓ(θ). Find by setting ∂ℓ/∂θ = 0"],
        ["MLE for Normal mean",   "ℓ(μ) = −n/2·log(2πσ²) − Σ(xᵢ−μ)²/(2σ²)\nMaximized at μ̂ = X̄; MLE for σ² = Σ(xᵢ−x̄)²/n (biased)"],
        ["Asymptotic normality",  "√n(θ̂−θ) →_d N(0, I(θ)⁻¹)  as n→∞\nAllows approximate CIs: θ̂ ± z_{α/2}/√I(θ̂)"],
        ["Invariance property",   "If θ̂ is MLE of θ, then g(θ̂) is MLE of g(θ)\nE.g. MLE of σ is √(MLE of σ²) — no re-optimization needed"],
        ["Bayesian vs MLE",       "MLE: point estimate maximizing likelihood\nMAP (Bayes): maximizes posterior ∝ likelihood × prior; reduces to MLE with flat prior"],
      ]} />

      <InfoBox
        title="Key Takeaways — Statistical Inference at a Glance"
        items={[
          "Sampling variability is the source of all uncertainty: the same procedure on a different sample gives a different result.",
          "Standard error SE = σ/√n quantifies precision of X̄; halving SE requires 4× the sample size.",
          "A 95% CI is a procedure that covers θ in 95% of repetitions — not a statement about one specific interval.",
          "p-value = P(data this extreme | H₀ true). A large p-value does NOT prove H₀; it only means insufficient evidence against it.",
          "Type I error (α) and Type II error (β) trade off: reducing one increases the other for fixed n.",
          "Power = 1−β: plan sample size before data collection using a priori power analysis.",
          "MLE is consistent, asymptotically Normal, and efficient — it achieves the Cramér–Rao lower bound asymptotically.",
          "All inference depends on model assumptions (normality, independence, etc.) — check them, and consider robust alternatives when violated.",
        ]}
      />
    </div>
  );
}